/**
 * Review an external certificate (plan docs/plans/lms-plan.md 7.5, 7.3, 5.5
 * lms_external_certs; Appendix A POST /admin/external-certs/:id/review;
 * 18.15, 18.21, 18.22; R17, R20; WP-45).
 *
 * The admin looks at the uploaded file, then accepts or rejects it with an
 * optional note to the uploader (a rejection should say why: the note is what
 * the learner sees), and signs with the credential re-check: the fixed
 * acceptance statement (7.3) for an acceptance, and the admin's password,
 * posted once with { _retry: true } (18.22). Accepting a certificate offered
 * against a requirement writes a completion of kind external that expires on
 * the certificate's own expiry (7.5). A wrong password records nothing.
 */
import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { chipCls, fieldCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { apiErrorCode, apiErrorMessage } from '../../../api/client';
import { getExternalCertUrl } from '../../../api/externalCerts';
import { EXTERNAL_ACCEPT_STATEMENT, REVIEW_NOTE_MAX, reviewExternalCert } from '../../../api/team';
import type { AdminExternalCert, ReviewDecision, ReviewExternalCertResult } from '../../../api/team';
import { openPendingTab } from '../../../components/player/DoneStage';
import { ErrorNote, formatDay, labelCls, linkBtnCls, metaCls, microLabelCls, personName } from '../../../components/pickers/shared';
import { PasswordField, signingErrorText } from '../signoffs/PasswordField';

export interface ReviewDialogProps {
  cert: AdminExternalCert;
  onCancel: () => void;
  onDone: (result: ReviewExternalCertResult, decision: ReviewDecision) => void;
}

/** Open the uploaded file in a new tab (presigned, 10 minutes; 7.5, 18.5). */
export function useOpenCertFile() {
  const { toast } = useToast();
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const open = async (certId: string) => {
    // Opened inside the click's user activation, before the request (18.27).
    const tab = openPendingTab();
    setBlockedUrl(null);
    try {
      const { url } = await getExternalCertUrl(certId, 'inline');
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlockedUrl(url);
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      toast(apiErrorMessage(err, 'The file could not be opened. Try again.'), { type: 'error' });
    }
  };
  return { open, blockedUrl };
}

export function ReviewDialog({ cert, onCancel, onDone }: ReviewDialogProps) {
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  const [note, setNote] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file = useOpenCertFile();
  const tooLong = note.length > REVIEW_NOTE_MAX;
  const ready = decision !== null && password.length > 0 && !tooLong && !busy;
  const holder = cert.holder ? personName(cert.holder) : 'this person';

  const submit = async () => {
    if (!ready || !decision) return;
    setBusy(true);
    setError(null);
    try {
      const result = await reviewExternalCert(cert.id, { decision, note, password });
      setPassword('');
      onDone(result, decision);
    } catch (err) {
      if (apiErrorCode(err) === 'LMS_REAUTH_FAILED') setPassword('');
      setError(signingErrorText(err, 'The review could not be recorded. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-4"
      data-testid="review-dialog"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className={microLabelCls}>Holder</dt>
          <dd className="text-gray-900 dark:text-gray-100">{holder}</dd>
        </div>
        <div>
          <dt className={microLabelCls}>Certificate</dt>
          <dd className="text-gray-900 dark:text-gray-100">{`${cert.title}, ${cert.issuer}`}</dd>
        </div>
        <div>
          <dt className={microLabelCls}>Issued</dt>
          <dd className="text-gray-900 dark:text-gray-100">{formatDay(cert.issued_on)}</dd>
        </div>
        <div>
          <dt className={microLabelCls}>Expires</dt>
          <dd className="text-gray-900 dark:text-gray-100">{cert.expires_on ? formatDay(cert.expires_on) : 'Does not expire'}</dd>
        </div>
        {cert.credential_id ? (
          <div>
            <dt className={microLabelCls}>Credential id</dt>
            <dd className="text-gray-900 dark:text-gray-100">{cert.credential_id}</dd>
          </div>
        ) : null}
        <div>
          <dt className={microLabelCls}>Offered against</dt>
          <dd className="text-gray-900 dark:text-gray-100">{cert.course ? cert.course.title : 'No requirement named'}</dd>
        </div>
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={outlineBtnCls()} onClick={() => void file.open(cert.id)} data-testid="review-view-file">
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          {`View ${cert.filename}`}
        </button>
        {file.blockedUrl ? (
          <a href={file.blockedUrl} target="_blank" rel="noopener noreferrer" className={linkBtnCls}>
            Open the file
          </a>
        ) : null}
      </div>

      <div>
        <span className={microLabelCls}>Decision</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <button
            type="button"
            className={chipCls(decision === 'accepted')}
            aria-pressed={decision === 'accepted'}
            onClick={() => setDecision('accepted')}
            data-testid="review-accept"
          >
            Accept
          </button>
          <button
            type="button"
            className={chipCls(decision === 'rejected')}
            aria-pressed={decision === 'rejected'}
            onClick={() => setDecision('rejected')}
            data-testid="review-reject"
          >
            Reject
          </button>
        </div>
        {decision === 'accepted' ? (
          <p className={metaCls}>
            {cert.course
              ? `Accepting records ${cert.course.title} as complete for ${holder}, valid until the certificate expires.`
              : 'Accepting keeps the certificate on the record; no requirement is completed because none was named.'}
          </p>
        ) : null}
      </div>

      <div>
        <label className={labelCls} htmlFor="review-note">
          {decision === 'rejected' ? 'Note to the uploader (say why)' : 'Note to the uploader (optional)'}
        </label>
        <textarea
          id="review-note"
          className={`${fieldCls} w-full`}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="review-note"
        />
        {tooLong ? <p className={metaCls}>{`At most ${REVIEW_NOTE_MAX} characters.`}</p> : null}
      </div>

      {decision === 'accepted' ? (
        <blockquote
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200"
          data-testid="review-statement"
        >
          {EXTERNAL_ACCEPT_STATEMENT}
        </blockquote>
      ) : null}

      <PasswordField id="review-password" value={password} onChange={setPassword} disabled={busy} testId="review-password" />

      {error ? <ErrorNote testId="review-error">{error}</ErrorNote> : null}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={secondaryBtnCls} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className={primaryBtnCls} disabled={!ready} data-testid="review-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {decision === 'rejected' ? 'Sign and reject' : decision === 'accepted' ? 'Sign and accept' : 'Sign'}
        </button>
      </div>
    </form>
  );
}

export default ReviewDialog;
